// SideBar.tsx
// ----------------------------------------------------------------
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { TreeView } from '@/components/ui/tree-view';
import { LOGOUT_IN_PROGRESS_FLAG, SIDEBAR_ITEMS } from '@/config/constants';
import { useUser } from '@/hooks/queries/auth';
import { useGetAllGroups } from '@/hooks/queries/groups';
import { useUnreadNotifications } from '@/hooks/queries/notification';
import { useGetCurrentUserDetails } from '@/hooks/queries/user';
import { getQueryClient } from '@/integrations/tanstack-query/RootProvider';
import { cn } from '@/lib/utils';
import useChatStore from '@/stores/useChatStore';
import useDrawerStore from '@/stores/useDrawerStore';
import useItemStore from '@/stores/useItem';
import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  useSidebar,
} from '@ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/tooltip';
import { ChevronRight, LogOut, Users } from 'lucide-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { UnreadCount } from '../wrappers/UnreadWrapper';
import { clearBrowserStorage } from '@/api/services/auth';
import { buildAuthentikEndSessionUrl } from '@/lib/authentikEndSession';
import MobileSidebarDrawerPane from '@/components/navigation/MobileSidebarDrawerPane';
// ----------------------------------------------------------------


/**
 * The SideBar component serves as the navigation menu for the application.
 * It displays a list of sidebar items, including folders and user information.
 * The active link is highlighted based on which drawer is currently open.
 * 
 * The component utilizes the Sidebar, SidebarContent, and other UI elements
 * from the @ui/sidebar library to create a collapsible sidebar layout.
 * 
 * It also includes a FolderTreeView for displaying folder structures and
 * a UserInfo component for showing user-related information.
 */
function SideBar() {
  const { data: allGroups } = useGetAllGroups();
  const allGroupsUnreadCount = useMemo(
    () => allGroups?.data?.reduce((acc, group) => acc + group.totalUnreadCount, 0) || 0,
    [allGroups]
  );

  // Extract store values and functions using Zustand selectors
  const isAiChatOpen = useChatStore(state => state.isAIChatOpen);
  const setAIChatOpen = useChatStore(state => state.setAIChatOpen);

  // Get drawer state and actions from Zustand store
  const activeDrawer = useDrawerStore(state => state.activeDrawer);
  const openDrawer = useDrawerStore(state => state.openDrawer);
  const closeDrawer = useDrawerStore(state => state.closeDrawer);

  // Logic to highlight the active sidebar item based on which drawer is open
  const activeLink = React.useMemo(() => {
    // Map drawer types to sidebar item keys
    if (activeDrawer === 'groups') return '/';
    if (activeDrawer === 'search') return '/search';
    if (activeDrawer === 'notifications') return '/notifications';
    if (activeDrawer === 'tasks') return '/tasks';
    if (activeDrawer === 'ai-chat' || isAiChatOpen) return 'openAiChat';

    // If no drawer is open, no item should be active
    return null;
  }, [isAiChatOpen, activeDrawer]);
  // Get the setOpen function to close the sidebar when a link is clicked.
  const { setOpen, open, isMobile, setOpenMobile } = useSidebar();

  /**
   * The handleCloseSidebar function is a callback function that closes the sidebar when a link is clicked.
   * It also toggles the appropriate drawer based on the clicked item.
   */
  const handleCloseSidebar = useCallback((to: string) => {
    if (!isMobile) {
      setOpen(false);
    }

    const drawerMap: Record<string, 'groups' | 'search' | 'notifications' | 'tasks' | 'ai-chat' | null> = {
      '/': 'groups',
      '/search': 'search',
      '/notifications': 'notifications',
      '/tasks': 'tasks',
      'openAiChat': 'ai-chat',
    };

    const drawerType = drawerMap[to];

    if (!drawerType) return;

    if (isMobile) {
      if (drawerType === 'ai-chat') {
        setAIChatOpen(true);
        openDrawer('ai-chat');
      } else {
        if (isAiChatOpen) {
          setAIChatOpen(false);
        }
        openDrawer(drawerType);
      }
      return;
    }

    if (drawerType === 'ai-chat') {
      if (activeDrawer === 'ai-chat') {
        setAIChatOpen(false);
        closeDrawer();
      } else {
        setAIChatOpen(true);
        openDrawer('ai-chat');
      }
    } else if (activeDrawer === drawerType) {
      return;

    } else {
      if (isAiChatOpen) {
        setAIChatOpen(false);
      }
      openDrawer(drawerType);
    }
  }, [isMobile, setOpen, activeDrawer, isAiChatOpen, setAIChatOpen, openDrawer, closeDrawer]);
  const groupId = useItemStore(state => state.groupId);
  const { data: notifications } = useUnreadNotifications();
  const isThereAnyUnreadNotification = useMemo(() => {
    const unreadCount = notifications?.data?.unreadNotificationCount?.unreadCount ?? 0;
    return unreadCount > 0;
  }, [notifications]);
  const showExpandedLabels = open && !isMobile;
  const shouldShowOpenAiButton = !!groupId;

  const renderSidebarNavItems = (items: ReadonlyArray<(typeof SIDEBAR_ITEMS)[number]>) => (
    <SidebarMenu className="w-full space-y-3">
      {items.map((item) => {
        const isActive = activeLink === item.to;
        return (
          <SidebarMenuItem key={item.to} className="w-full">
            <SidebarMenuButton asChild>
              <Tooltip delayDuration={600}>
                <TooltipTrigger asChild>
                  <div>
                    <SideBarActionButton
                      to={item.to}
                      className={cn(
                        (!open || isMobile) && 'justify-center',
                        isActive && 'active group-active'
                      )}
                      isLink={false}
                      onClick={() => handleCloseSidebar(item.to)}
                    >
                      {item?.label === 'Inbox' && isThereAnyUnreadNotification && (
                        <Badge className="bg-primary-p-main absolute top-0 right-1 h-4 w-4 rounded-full p-0" />
                      )}
                      {isActive && item.activeIcon ? (
                        <item.activeIcon
                          className={cn(
                            'h-6 w-6 text-[var(--color-info-I-dark)] dark:text-[var(--color-info-I-dark)]',
                            !['Inbox', 'KI Assistent'].includes(item?.label) &&
                            'fill-[var(--color-info-I-dark)] dark:fill-[var(--color-info-I-dark)]'
                          )}
                        />
                      ) : (
                        <item.icon
                          className={cn(
                            'h-6 w-6 text-gray-600 dark:text-gray-400',
                            'group-[.active]:!text-[var(--color-info-I-dark)] dark:group-[.active]:!text-[var(--color-info-I-dark)]',
                            !['Inbox', 'KI Assistent'].includes(item?.label) &&
                            'group-[.active]:!fill-[var(--color-info-I-dark)] dark:group-[.active]:!fill-[var(--color-info-I-dark)]'
                          )}
                        />
                      )}
                      {showExpandedLabels && (
                        <span
                          className={cn(
                            'text-sm font-medium text-gray-700 group-[.active]:text-[var(--color-info-I-dark)] dark:text-gray-300 dark:group-[.active]:text-[var(--color-info-I-dark)]'
                          )}
                        >
                          {item.label}
                        </span>
                      )}
                      {item.label === 'Groups' && allGroupsUnreadCount > 0 ? (
                        <UnreadCount
                          count={allGroupsUnreadCount}
                          className={`${!showExpandedLabels ? 'absolute -top-0 right-1' : ''} h-4 w-4 rounded-full p-0 !text-[9.5px]`}
                        />
                      ) : null}
                    </SideBarActionButton>
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  hidden={showExpandedLabels}
                  side="right"
                  className="flex items-center gap-1 bg-gray-800 text-sm text-white shadow-lg dark:bg-gray-700 dark:text-gray-100"
                  arrowClassName="fill-gray-800 dark:fill-gray-700"
                >
                  <p>{item.label}</p>
                  {item.label === 'Groups' && allGroupsUnreadCount > 0 ? (
                    <UnreadCount
                      count={allGroupsUnreadCount}
                      className="h-4 w-4 rounded-full p-0 !text-[9.5px] font-medium"
                    />
                  ) : null}
                </TooltipContent>
              </Tooltip>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  const sidebarNavMenu = renderSidebarNavItems(
    SIDEBAR_ITEMS.filter((item) => item.to !== 'openAiChat')
  );

  const sidebarAiChatMenu = shouldShowOpenAiButton
    ? renderSidebarNavItems(SIDEBAR_ITEMS.filter((item) => item.to === 'openAiChat'))
    : null;

  return (
    <Sidebar
      collapsible="icon"
      side="left"
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toast.error('File drop is disabled in this area');
      }}
    >
      {isMobile ? (
        <div className="flex h-full min-h-0 w-full">
          <div className="bg-sidebar text-sidebar-foreground flex w-(--sidebar-width-icon) shrink-0 flex-col border-r border-gray-200 dark:border-gray-700">
            <SidebarContent className="mt-4 w-full flex-1">
              <div className="flex h-full w-full flex-col space-y-3 px-1 pt-3">
                {sidebarNavMenu}
              </div>
            </SidebarContent>
            <SidebarFooter>
              {sidebarAiChatMenu}
              <SidebarMenuButton asChild>
                <SideBar.UserInfo />
              </SidebarMenuButton>
            </SidebarFooter>
          </div>
          <MobileSidebarDrawerPane />
        </div>
      ) : (
        <>
          <SidebarContent className="mt-4 w-full">
            <div className="flex h-full w-full flex-col space-y-3 pt-3">
              {sidebarNavMenu}
              {open && <SidebarSeparator className="mx-auto" />}
              {open && (
                <SidebarMenu className="flex-grow overflow-y-auto p-0">
                  <SideBar.FolderTreeView />
                </SidebarMenu>
              )}
            </div>
          </SidebarContent>
          <SidebarFooter>
            {sidebarAiChatMenu}
            <SidebarMenuButton asChild>
              <SideBar.UserInfo />
            </SidebarMenuButton>
          </SidebarFooter>
        </>
      )}
    </Sidebar>
  );
}
/**
 * SideBarActionButton component that renders either as a button or a link
 * If href is provided, renders as a link; otherwise renders as a button with onClick
 */
const SideBarActionButton = (props: React.ComponentProps<'button' | typeof Link> & {
  isLink?: boolean;
  to?: string;
  onClick?: () => void;
}) => {
  const initialClassName =
    'h-10 w-full p-2 flex items-center justify-start gap-2 cursor-pointer group m-0 rounded-md transition-colors text-gray-600 dark:text-gray-400 hover:!text-sidebar-foreground hover:bg-white/50 dark:hover:bg-gray-800/50 [.active]:!text-[var(--color-info-I-dark)] [.active]:!fill-[var(--color-info-I-dark)] [.active]:bg-[var(--color-surface-light-sl-blue)] dark:[.active]:bg-[var(--color-surface-light-sl-blue)]/30 [.active]:shadow-sm';
  const { isLink = false, onClick, className, to, children, ...buttonProps } = props;
  // If href is provided or isLink is true, render as a link
  if (isLink) {
    return (
      <Link
        to={to || '/'}
        className={cn(initialClassName, className)}
        onClick={() => onClick?.()}
      >
        {children}
      </Link>
    );
  }

  // Otherwise render as a button
  return (
    <button
      {...(buttonProps as React.ComponentProps<'button'>)}
      className={cn(initialClassName, className)}
      onClick={() => onClick?.()}
    >
      {children as React.ReactNode}
    </button>
  );
};

/**
 * The SideBar.Provider component is a wrapper that provides the SidebarProvider context to its children.
 * This allows the Sidebar component to be used within the application.
 */
SideBar.Provider = ({ children }: { children: React.ReactNode }) => {
  return (
    <SidebarProvider defaultOpen={false} open={false}>
      {children}
    </SidebarProvider>
  );
};

/**
 * The SideBar.UserInfo component displays user information.
 * It shows user details and provides options for logout.
 */
SideBar.UserInfo = () => {
  const user = useUser(state => state.user);
  const setUser = useUser(state => state.setUser);
  const logoutUser = useUser(state => state.logoutUser);
  const { data: currentUserDetails, isPending } = useGetCurrentUserDetails();
  const { open } = useSidebar();
  // const { mutateAsync: logout } = useLogoutMutation();

  // Extract chat store functions using Zustand selectors
  const setAIChatOpen = useChatStore(state => state.setAIChatOpen);
  const setMemberChatOpen = useChatStore(state => state.setMemberChatOpen);

  const handleLogout = async () => {
    // debugger;
    const { queryClient } = getQueryClient();
    try {
      await queryClient.cancelQueries();
    } finally {
      queryClient.clear();
      setAIChatOpen(false);
      setMemberChatOpen(true);
      const logoutUrl = buildAuthentikEndSessionUrl();
      if (!logoutUrl) {
        console.error('[Logout] Missing end-session URL or VITE_AUTHENTIK_CLIENT_ID');
        return;
      }

      clearBrowserStorage();
      sessionStorage.setItem(LOGOUT_IN_PROGRESS_FLAG, 'true');
      // logoutUser();
      window.location.replace(logoutUrl.toString());
      // Redirect with forceLogin=true to force re-authentication
      // This adds prompt=login to the OAuth request, clearing Authentik's cached session
      // navigate({
      //   to: 'https://oauth.relas.eu/application/o/mani/end-session/',
      // });
    }
  };

  const userInitials = useMemo(() => {
    if (isPending) return user?.firstName?.charAt(0) || 'U';
    if (!currentUserDetails?.data?.firstName && !currentUserDetails?.data?.lastName) {
      return currentUserDetails?.data?.email?.charAt(0).toUpperCase() || 'U';
    }
    return `${currentUserDetails?.data?.firstName?.charAt(0) || ''}${currentUserDetails?.data?.lastName?.charAt(0) || ''}`.toUpperCase();
  }, [user, currentUserDetails, isPending]);

  const displayName = useMemo(() => {
    if (isPending) return user?.firstName || user?.email?.split('@')[0] || 'User';
    if (!currentUserDetails?.data?.firstName && !currentUserDetails?.data?.lastName) {
      return currentUserDetails?.data?.email?.split('@')[0] || 'User';
    }
    return currentUserDetails?.data?.firstName && currentUserDetails?.data?.lastName
      ? `${currentUserDetails.data.firstName} ${currentUserDetails.data.lastName}`
      : currentUserDetails?.data?.firstName ||
      currentUserDetails?.data?.email?.split('@')[0] ||
      'User';
  }, [user, isPending, currentUserDetails]);

  useEffect(() => {
    const latestUser = currentUserDetails?.data;
    if (!latestUser) return;

    const hasMismatch =
      String(user?.id ?? '') !== String(latestUser.id ?? '') ||
      (user?.email ?? '') !== (latestUser.email ?? '') ||
      (user?.firstName ?? '') !== (latestUser.firstName ?? '') ||
      (user?.lastName ?? '') !== (latestUser.lastName ?? '') ||
      user?.isEnabled !== latestUser.isEnabled ||
      user?.isBlocked !== latestUser.isBlocked;

    if (!hasMismatch) return;

    setUser({
      id: String(latestUser.id),
      username: latestUser.username,
      firstName: latestUser.firstName,
      lastName: latestUser.lastName,
      email: latestUser.email,
      mobileNo: latestUser.mobileNo,
      isEnabled: latestUser.isEnabled,
      isBlocked: latestUser.isBlocked,
      role: {
        id: String(latestUser.role?.id ?? ''),
        name: latestUser.role?.name ?? '',
      },
      organization: {
        id: String(latestUser.organization?.id ?? ''),
        name: latestUser.organization?.name ?? '',
      },
    });
  }, [currentUserDetails, setUser, user]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'h-auto w-full cursor-pointer rounded-md p-2 text-left transition-colors focus:ring-1',
            !open && 'justify-center',
            open && 'bg-sidebar-accent/20 hover:bg-sidebar-accent/50'
          )}
        >
          <div className="flex w-full items-center gap-3 justify-center">
            {/* User Avatar with Status */}
            <div className="relative">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-sidebar-foreground text-sidebar border-sidebar-accent/50 border text-sm font-semibold">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* User Info */}
            {open && (
              <span className={'text-sidebar-foreground w-full truncate text-sm font-semibold'}>
                {displayName}
              </span>
            )}
            {open && <ChevronRight className="h-5 w-5" />}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" side="right">
        <DropdownMenuArrow />
        <DropdownMenuLabel>
          <div className="flex items-start gap-3">
            {/* User Avatar */}
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-sidebar-foreground text-sidebar border-sidebar-accent/50 border text-sm font-semibold">
                {userInitials}
              </AvatarFallback>
            </Avatar>

            {/* User Info - Name and Email in column */}
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <span className="text-foreground w-full truncate text-sm font-semibold">
                {displayName}
              </span>
              <span className="text-muted-foreground w-full truncate text-xs">
                {currentUserDetails?.data?.email || 'No email'}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* <DropdownMenuItem onClick={handleSettings} className="cursor-pointer">
          <Settings className="mr-2 h-4 w-4" />
          <span>Settings</span>
        </DropdownMenuItem> */}

        {/* <DropdownMenuSeparator /> */}

        <DropdownMenuItem
          onClick={handleLogout}
          className="text-destructive focus:text-destructive hover:!bg-error-E-dark/10 cursor-pointer"
        >
          <LogOut className="text-destructive mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * The SideBar.FolderTreeView component is a tree view that displays the folder structure.
 * It uses the useGetAllFolders hook to get the folder structure and the useGetFolderById hook to get the folder details.
 * It also uses the useNavigate hook to navigate to the folder page when a folder is clicked.
 */
SideBar.FolderTreeView = () => {
  // Get the folder structure and the folder details from API.
  const { data, isPending } = useGetAllGroups();
  const { isMobile, setOpenMobile } = useSidebar();
  // Get the navigate function to navigate to the folder page when a folder is clicked.
  const navigate = useNavigate();
  // Get the folderId from the URL.
  const { groupId } = useParams({ strict: false });
  // ! OLD CODE: Used to get children of a folder (not GROUP). (until an API is developed)
  // Get the folder details from API.
  // const { data: folderDetails, isPending: isFolderDetailsLoading } = useGetFolderById(
  //   folderId || ''
  // );
  // Get the tree view from the folder structure.
  // const folderView = React.useMemo(() => {
  //   if (!data || !data?.data) return [];
  //   // * Folders and Tasks are treated as folders in the tree view.
  //   const folders = data?.data?.childItems?.filter((item) =>
  //     ['folder', 'task'].includes(item?.itemType?.toLowerCase())
  //   );
  //   const subFolders = folderDetails?.data
  //     ? folderDetails?.data?.childItems?.filter((i) =>
  //         ['folder', 'task'].includes(i?.itemType?.toLowerCase())
  //       ) || []
  //     : [];
  //   // Get the tree view from the subfolders.
  //   const res = folders?.map((item) => {
  //     return {
  //       id: Number(item.id),
  //       name: (item as FolderItem).name,
  //       ...(item.totalUnreadCount > 0 && {
  //         actions: (
  //           <UnreadCount count={item.totalUnreadCount} className="group-hover:none !text-[8px]" />
  //         ),
  //       }),
  //       onClick: () => {
  //         navigate({
  //           to: `/folder/$folderId`,
  //           params: {
  //             folderId: String(item.id),
  //           },
  //         });
  //       },
  //     };
  //   }) as unknown as Array<TreeDataItem>;

  //   !isFolderDetailsLoading &&
  //     subFolders?.length > 0 &&
  //     res?.forEach((folder) => {
  //       if (folder.id === Number(folderId)) {
  //         const alreadyAdded = new Set(folder?.children?.map((i) => i?.id) || []);
  //         if (folder)
  //           folder.children = [
  //             ...(folder.children || []),
  //             ...(
  //               subFolders?.map((folder) => {
  //                 if (alreadyAdded.has(folder.id)) return null;
  //                 // * If the item type is TASK, then the title is the title of the task else its the name of the folder.
  //                 const title = folder?.itemType === 'TASK' ? folder?.title : (folder as FolderItem)?.name;
  //                 return {
  //                   id: Number(folder.id),
  //                   name: title,
  //                   ...(folder.totalUnreadCount > 0 && {
  //                     actions: (
  //                       <UnreadCount
  //                         count={folder.totalUnreadCount}
  //                         className="group-hover:none !text-[8px]"
  //                       />
  //                     ),
  //                   }),
  //                   onClick: () => {
  //                     navigate({
  //                       to: `/folder/$folderId`,
  //                       params: {
  //                         folderId: String(folder.id),
  //                       },
  //                     });
  //                   },
  //                 };
  //               }) || []
  //             ).filter(Boolean),
  //           ] as unknown as Array<TreeDataItem>;
  //       }
  //     });
  //   return res;
  // }, [data, folderDetails, isFolderDetailsLoading]);
  const folderView = data?.data?.map((item) => {
    return {
      id: item.id,
      name: item.name,
      startIcon: Users,
      onClick: () => {
        if (isMobile) {
          setOpenMobile(false);
        }
        navigate({
          to: `/group/$groupId/item/$folderId`,
          params: {
            folderId: String(-1),
            groupId: String(item.id),
          },
        });
      },
    };
  });

  // If the folder structure is loading, return a loading state.
  if (isPending) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={index} className="bg-background/40 my-3 h-6 w-full animate-pulse rounded-sm" />
        ))}
      </div>
    );
  } else {
    // If the folder structure is loaded, return the tree view.
    return (
      <TreeView
        className="m-0 p-0 !text-white max-h-[calc(100vh-100px)] overflow-y-auto no-scrollbar"
        // leafClassName="hover:bg-white"
        data={folderView || []}
        initialSelectedItemId={groupId ?? ''}
        defaultNodeIcon={ChevronRight}
        defaultLeafIcon={ChevronRight}
      />
    );
  }
};
export default SideBar;
